import React, { useState, useRef, useEffect } from 'react';
import { sectorData } from '../../config/sectorData';
import {
  calculateTotalImpactReduction
  // Remove unused imports
} from '../../utils/calculationHelpers';
import { NET_ZERO_EXPLANATION_MATRIX } from '../../config/netZeroExplanations';
import { DASHBOARD_COLORS, getSectorColor } from '../../config/colorScheme';

import ToggleSwitch from './ToggleSwitch';
import QuickChoicesPanel from './QuickChoicesPanel';
import QuickChoicesButton from './QuickChoicesButton';
import BottomNavigation from './BottomNavigation';
import SectorAccordion from './SectorAccordion';
import ResultsTabs from './ResultsTabs';
import DashboardHeader from './DashboardHeader';
// Remove unused import
import { SCENARIO_CONFIGS, getScenarioConfig } from '../../config/scenarioConfigs';
import { SCENARIO_LOOKUP,getScenarioLookup  } from '../../config/scenarioConfigs';
import { SCENARIO_DESCRIPTIONS } from '../../config/scenarioDescriptions';

// Import images
import currentScenarioImage from '../assets/sc-current-bg-image.jpg';
import steerScenarioImage from '../assets/sc-steer-bg-image.jpg';
import shiftScenarioImage from '../assets/sc-shift-bg-image.jpg';
import transformScenarioImage from '../assets/sc-transform-bg-image.jpg';

// Scenario Background Configuration
const SCENARIO_BACKGROUNDS = {
  current: {
    image: currentScenarioImage,
    backgroundColor: '#043D46',
    overlay: 'rgba(0,0,0,0.3)'
  },
  steer: {
    image: steerScenarioImage,
    backgroundColor: '#043D46',
    overlay: 'rgba(0,0,0,0.3)'
  },
  shift: {
    image: shiftScenarioImage,
    backgroundColor: '#043D46',
    overlay: 'rgba(0,0,0,0.3)'
  },
  transform: {
    image: transformScenarioImage,
    backgroundColor: '#043D46',
    overlay: 'rgba(0,0,0,0.3)'
  }
};



const DashboardWireframe = () => {
  const [activeTab, setActiveTab] = useState('impact');

  // Modify initial scenario index to be the index of 'steer' scenario
  const scenarioKeys = Object.keys(SCENARIO_CONFIGS).filter(key => key !== 'initial');
  const initialScenarioIndex = scenarioKeys.findIndex(key => key === 'steer');

  const [activeScenarioIndex, setActiveScenarioIndex] = useState(initialScenarioIndex);

  // Add ambition level state (low/high)
  const [ambitionLevel, setAmbitionLevel] = useState('low');

  // State for sector population impact
  const [sectorImpact, setSectorImpact] = useState(
    SCENARIO_CONFIGS.steer.low // Default to steer scenario with low ambition
  );

  // State for comparison scenario impact
  const [comparisonSectorImpact, setComparisonSectorImpact] = useState({
    food: 'high',
    buildings: 'high',
    transport: 'high',
    industry: 'high'
  });

  // State for bottom navigation
  const [bottomNavActive, setBottomNavActive] = useState('choices');

  // State for quick choices modal
  const [isQuickChoicesOpen, setIsQuickChoicesOpen] = useState(false);

  // State to control QuickChoicesButton visibility
  const [isQuickChoicesButtonVisible, setIsQuickChoicesButtonVisible] = useState(false);

  // Add comparisonMode state
  const [comparisonMode, setComparisonMode] = useState(false);

  // Refs for scrolling and tracking sections
  const choicesRef = useRef(null);
  const tradeoffsRef = useRef(null);
  const impactsRef = useRef(null);
  const sectorsRef = useRef(null);
  const resultsRef = useRef(null);



  // Effect to track scroll and show QuickChoicesButton
  useEffect(() => {
    let hasPassedResultsSection = false;

    const handleScroll = () => {
      if (!resultsRef.current) return;

      const resultsSection = resultsRef.current;
      const rect = resultsSection.getBoundingClientRect();

      // Check if the top of the results section has passed the top of the viewport
      if (rect.top <= 0) {
        hasPassedResultsSection = true;
      }

      // Check if we're at the bottom of the page
      const isAtBottomOfPage =
        (window.innerHeight + window.scrollY) >= document.body.offsetHeight;

      // Check if scrolling back up above the results section
      const isScrolledBackAboveResults = rect.top > 0;

      // Set visibility based on whether we've passed the results section
      // and haven't scrolled back up, or if we're at the bottom of the page
      setIsQuickChoicesButtonVisible(
        (hasPassedResultsSection && !isScrolledBackAboveResults) ||
        isAtBottomOfPage
      );
    };

    // Add scroll event listener
    window.addEventListener('scroll', handleScroll);

    // Initial check
    handleScroll();

    // Cleanup
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  // Function to scroll to section
  const scrollToSection = (section) => {
    const sectionRefs = {
      choices: choicesRef,
      tradeoffs: tradeoffsRef,
      impacts: impactsRef,
      sectors: sectorsRef,
      'your-choices': choicesRef
    };

    const targetRef = sectionRefs[section];

    // Try scrolling with ref
    if (targetRef && targetRef.current) {
      targetRef.current.scrollIntoView({ behavior: 'smooth' });
      setBottomNavActive(section);

      // Additional tab logic for impacts section
      if (section === 'impacts') {
        setActiveTab('impact');
      } else if (section === 'sectors') {
        setActiveTab('sectors');
      }
      return;
    }

    // Fallback to document.getElementById
    const element = document.getElementById(section);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      return;
    }

    // If both methods fail, log a warning
    console.warn(`Could not scroll to section: ${section}`);
  };

  // Function to update sector impact
  const updateSectorImpact = (sectorOrConfig, impact, isComparison = false) => {
    // Check if the input is a full configuration object
    if (typeof sectorOrConfig === 'object' && sectorOrConfig !== null) {
      // If it's a scenario configuration, update all sectors
      if (isComparison) {
        setComparisonSectorImpact(sectorOrConfig);
      } else {
        setSectorImpact(sectorOrConfig);
      }
      return;
    }

    // Validate input
    const validImpacts = ['current', 'low', 'high'];
    if (!validImpacts.includes(impact)) {
      console.warn(`Invalid impact state: ${impact}`);
      return;
    }

    if (isComparison) {
      setComparisonSectorImpact(prev => ({
        ...prev,
        [sectorOrConfig]: impact
      }));
    } else {
      setSectorImpact(prev => ({
        ...prev,
        [sectorOrConfig]: impact
      }));
    }
  };

  // pull the copy from your lookup (safe fallback)
// const { copy: scenarioCopy = '' } =
//     const currentScenarioKey =
//   scenarioKeys[activeScenarioIndex] ?? 'current';

// const { copy: scenarioCopy = '' } =
//   getScenarioLookup(currentScenarioKey, ambitionLevel);

  const generateNetZeroEquationExplanation = (sectorImpact) => {
    // Convert sector impacts to a key string
    const impactKey = [
      sectorImpact.food,
      sectorImpact.buildings,
      sectorImpact.transport,
      sectorImpact.industry
    ]
    .map(impact => impact === 'high' ? 'high' : 'low')
    .join('-');

    console.log('Current Sector Impacts:', sectorImpact);
    console.log('Generated Impact Key:', impactKey);

    // Return the corresponding explanation or a default message
    const explanation = NET_ZERO_EXPLANATION_MATRIX[impactKey] ||
      "Your choices present a unique pathway to net zero. Each sector's impact contributes to a complex and nuanced approach to emissions reduction.";

    return explanation;
  };



  // Function to calculate total impact percentage reduction
  const calculateTotalImpactReduction = (sectorImpact) => {
    // Baseline UK emissions in 2020
    const baselineEmissions = 450;

    // Sector reduction multipliers with current, low, and high states
    const sectorMultipliers = {
      food: {
        current: 1.0,
        low: 0.85,
        high: 0.7
      },
      buildings: {
        current: 1.0,
        low: 0.75,
        high: 0.6
      },
      transport: {
        current: 1.0,
        low: 0.75,
        high: 0.5
      },
      industry: {
        current: 1.0,
        low: 0.8,
        high: 0.65
      }
    };

    // Initial sector emissions based on baseline
    const initialSectorEmissions = {
      food: baselineEmissions * 0.25,
      buildings: baselineEmissions * 0.30,
      transport: baselineEmissions * 0.20,
      industry: baselineEmissions * 0.25
    };

    // Calculate final sector emissions
    const finalSectorEmissions = Object.keys(initialSectorEmissions).reduce((acc, sector) => {
      const sectorFactor = sectorMultipliers[sector][sectorImpact[sector] || 'current'];
      acc[sector] = initialSectorEmissions[sector] * sectorFactor;
      return acc;
    }, {});

    // Calculate total final emissions
    const finalTotalEmissions = Object.values(finalSectorEmissions).reduce((a, b) => a + b, 0);

    // Calculate total percentage reduction
    const totalReductionPercentage = Math.round((1 - finalTotalEmissions / baselineEmissions) * 100);

    console.log('Total Impact Reduction Calculation:', {
      sectorImpact,
      initialSectorEmissions,
      finalSectorEmissions,
      finalTotalEmissions,
      baselineEmissions,
      totalReductionPercentage
    });

    return {
      totalReductionPercentage,
      finalTotalEmissions
    };
  };

  // Replace calculateTotalImpact with new percentage-based calculation
  const calculateTotalImpact = (sectorImpact) => {
    const { totalReductionPercentage } = calculateTotalImpactReduction(sectorImpact);
    return totalReductionPercentage;
  };

  // Render comparison section
  const renderComparisonSection = () => {
    if (!comparisonMode) return null;

    return (
      <div className="bg-gray-50 p-4 rounded-lg border mb-6">
        <h3 className="text-sm font-medium text-gray-700 mb-3">Scenario Comparison</h3>
        <div className="grid grid-cols-2 gap-4">
          {/* Primary Scenario */}
          <div className="bg-white p-4 rounded-lg border">
            <h4 className="text-sm font-semibold mb-2">Your Current Scenario</h4>
            <div className="space-y-2">
              {Object.keys(sectorImpact).map(sector => (
                <div key={sector} className="flex justify-between items-center">
                  <span className="text-xs capitalize">{sector} Sector</span>
                  <span className={`text-xs font-medium ${sectorImpact[sector] === 'high' ? 'text-green-600' : 'text-gray-500'}`}>
                    {sectorImpact[sector] === 'high' ? 'High Impact' : 'Low Impact'}
                  </span>
                </div>
              ))}
              <div className="mt-2 border-t pt-2">
                <div className="flex justify-between text-xs">
                  <span>Total Impact:</span>
                  <span className="font-bold text-green-600">
                    {calculateTotalImpact(sectorImpact)}% Reduction
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Comparison Scenario */}
          <div className="bg-white p-4 rounded-lg border">
            <h4 className="text-sm font-semibold mb-2">Comparison Scenario</h4>
            <div className="space-y-2">
              {Object.keys(comparisonSectorImpact).map(sector => (
                <div key={sector} className="flex justify-between items-center">
                  <span className="text-xs capitalize">{sector} Sector</span>
                  <ToggleSwitch
                    isOn={comparisonSectorImpact[sector] === 'high'}
                    onToggle={() => updateSectorImpact(sector,
                      comparisonSectorImpact[sector] === 'low' ? 'high' : 'low',
                      true
                    )}
                    labelOff="Low"
                    labelOn="High"
                  />
                </div>
              ))}
              <div className="mt-2 border-t pt-2">
                <div className="flex justify-between text-xs">
                  <span>Total Impact:</span>
                  <span className="font-bold text-green-600">
                    {calculateTotalImpact(comparisonSectorImpact)}% Reduction
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Comparison Insights */}
        <div className="mt-4 bg-blue-50 p-3 rounded-lg border">
          <h4 className="text-xs font-medium text-blue-800 mb-1">Comparison Insights</h4>
          <p className="text-xs">
            Comparing your current scenario with an alternative approach helps visualize the potential impact of different choices across sectors.
          </p>
        </div>
      </div>
    );
  };

  // Function to handle scenario carousel navigation
  const handleScenarioNavigation = (direction) => {
    const totalScenarios = scenarioKeys.length;
    setActiveScenarioIndex((prevIndex) => {
      let newIndex = prevIndex;

      if (direction === 'next') {
        newIndex = (prevIndex + 1) % totalScenarios;
      } else {
        newIndex = (prevIndex - 1 + totalScenarios) % totalScenarios;
      }

      // Ensure we don't select the 'initial' scenario
      const selectedScenario = scenarioKeys[newIndex];
      if (selectedScenario === 'initial') {
        // If 'initial' is selected, move to the next scenario
        newIndex = (newIndex + 1) % totalScenarios;
      }

      return newIndex;
    });
  };


  // Function to select the currently focused scenario
  const handleScenarioSelect = () => {
    const selectedScenario = scenarioKeys[activeScenarioIndex];

    // Use current ambition level
    const scenarioVariant = ambitionLevel;

    // Get the scenario configuration
    const scenarioConfig = getScenarioConfig(selectedScenario, scenarioVariant);

    // Get the scenario lookup
    // const scenarioLookup = getScenarioLookup(selectedScenario, scenarioVariant);

    // Explicitly set the sector impact to the scenario configuration
    setSectorImpact(scenarioConfig);

    // Additional logging for debugging
    // console.log('Selected Scenario:', selectedScenario);
    // console.log('Ambition Level:', scenarioVariant);
    // console.log('Scenario Config:', scenarioConfig);

    // Ensure the scenario is set correctly
    navigateToScenario(selectedScenario, scenarioVariant);
  };

  // Get background for current scenario
  const getCurrentScenarioBackground = () => {
    // If no scenario is selected, return default background
    if (activeScenarioIndex === null) {
      return SCENARIO_BACKGROUNDS.current;
    }

    const currentScenario = scenarioKeys[activeScenarioIndex];
    const scenarioBackground = SCENARIO_BACKGROUNDS[currentScenario];
    return scenarioBackground || SCENARIO_BACKGROUNDS.current;
  };

//   const { copy: scenarioCopy } = getScenarioLookup(currentScenario, ambitionLevel);
// const currentScenarioKey =
//   scenarioKeys[activeScenarioIndex] ?? 'current';

// // pull the copy from your lookup (safe fallback)
// const { copy: scenarioCopy = '' } =
//   getScenarioLookup(currentScenarioKey, ambitionLevel);

// pick the scenario whose config exactly matches sectorImpact for the current ambition
const inferScenarioFromImpact = (sectorImpact, ambitionLevel) => {
  for (const [scenario, variants] of Object.entries(SCENARIO_CONFIGS)) {
    const cfg = variants?.[ambitionLevel];
    if (!cfg) continue;
    const matches = Object.entries(cfg).every(
      ([sector, impact]) => sectorImpact[sector] === impact
    );
    if (matches) return scenario; // "current" | "steer" | "shift" | "transform"
  }
  return null;
};

const currentScenarioKey = scenarioKeys[activeScenarioIndex] ?? 'current';

const inferredScenarioKey = React.useMemo(
  () => inferScenarioFromImpact(sectorImpact, ambitionLevel) ?? currentScenarioKey,
  [sectorImpact, ambitionLevel, currentScenarioKey]
);

// use inferred for the copy
const { copy: scenarioCopy = '' } = getScenarioLookup(inferredScenarioKey, ambitionLevel);

  // Function to navigate to a specific scenario
  const navigateToScenario = (scenarioName, variant = null) => {
    const scenarioIndex = scenarioKeys.findIndex(key => key === scenarioName);
    if (scenarioIndex !== -1) {
      // Set the active scenario index
      setActiveScenarioIndex(scenarioIndex);

      // Use current ambition level if no variant specified
      const finalVariant = variant || ambitionLevel;

      // Explicitly set the sector impact to the scenario configuration
      const scenarioConfig = getScenarioConfig(scenarioName, finalVariant);
      setSectorImpact(scenarioConfig);

      // Log for debugging
      console.log('Navigating to Scenario:', scenarioName);
      console.log('Ambition Level:', finalVariant);
      console.log('Scenario Index:', scenarioIndex);
      console.log('Scenario Config:', scenarioConfig);
    }
  };

  // Function to handle ambition level change
  const handleAmbitionLevelChange = (newAmbitionLevel) => {
    setAmbitionLevel(newAmbitionLevel);

    // Update sector impact with current scenario and new ambition level
    const currentScenario = scenarioKeys[activeScenarioIndex];
    const scenarioConfig = getScenarioConfig(currentScenario, newAmbitionLevel);
    setSectorImpact(scenarioConfig);

    console.log('Ambition Level Changed:', newAmbitionLevel);
    console.log('Current Scenario:', currentScenario);
    console.log('New Config:', scenarioConfig);
  };

  // Helper function to get display name for scenarios
  const getScenarioDisplayName = (scenario) => {
    if (scenario === 'net_zero_futures_dvl_shift_high_transstshiftec_2001') {
      return "Citizen's Panel";
    }
    return scenario;
  };

  // Render scenario selection buttons
  const renderScenarioSelectionButtons = () => {
  const scenarios = scenarioKeys.filter(
    (s) => s !== "initial" && s !== "current"
  );

  return (
    <div className="flex flex-wrap items-center gap-2 sm:gap-3 lg:gap-4 mb-4 sm:mb-6 lg:mb-8 mt-2 sm:mt-3 lg:mt-4 w-full">
      {scenarios.map((scenario, index) => {
        const isLast = index === scenarios.length - 1;
        const isActive =
          activeScenarioIndex !== null &&
          scenarioKeys[activeScenarioIndex] === scenario;
        const displayName = getScenarioDisplayName(scenario);

        return (
          <React.Fragment key={scenario}>
            {/* Spacer that pushes the last button to the right */}
            {isLast && <div className="flex-grow" />}

            <button
              onClick={() => navigateToScenario(scenario, ambitionLevel)}
              className={`
                px-3 sm:px-4 lg:px-6 py-2 sm:py-2.5 lg:py-3 rounded-lg transition-all duration-300
                capitalize font-medium text-sm sm:text-base lg:text-lg
                text-white border border-white border-opacity-20
                whitespace-nowrap min-w-[80px] sm:min-w-[100px]
                ${isActive ? "underline underline-offset-4 bg-white bg-opacity-10" : "hover:opacity-80"}
              `}
            >
              {displayName}
            </button>
          </React.Fragment>
        );
      })}
    </div>
  );
};



 const renderPanelScenarioSelectionButtons = () => {
  const scenario = "net_zero_futures_dvl_shift_high_transstshiftec_2001";
  const ambitionLevel = "low"; // keep/change as needed
  const isActive = false;

  return (
    <div className="flex flex-wrap justify-center gap-2 sm:gap-3 lg:gap-4 mb-4 sm:mb-6 lg:mb-8 mt-2 sm:mt-3 lg:mt-4">
      <button
        key="panel"
        onClick={() => navigateToScenario(scenario, ambitionLevel)}
        className={`
          px-3 sm:px-4 lg:px-6 py-2 sm:py-2.5 lg:py-3 rounded-lg transition-all duration-300
          font-medium text-sm sm:text-base lg:text-lg
          text-white border border-white border-opacity-20
          whitespace-nowrap min-w-[80px] sm:min-w-[100px]
          ${isActive ? "underline underline-offset-4 bg-white bg-opacity-10" : "hover:opacity-80"}
        `}
      >
        Final citizens’ panel scenario
      </button>
    </div>
  );
};


  // Render ambition level selection
  const renderAmbitionLevelSelection = () => {
    return (
      <div className="flex flex-wrap justify-center gap-2 sm:gap-3 lg:gap-4 mb-4 sm:mb-6 lg:mb-8 mt-2 sm:mt-3 lg:mt-4">
        {['low', 'high'].map((level) => (
          <button
            key={level}
            onClick={() => handleAmbitionLevelChange(level)}
            className={`
              px-3 sm:px-4 lg:px-6 py-2 sm:py-2.5 lg:py-3 rounded-lg transition-all duration-300
              capitalize font-medium text-sm sm:text-base lg:text-lg
              text-white border border-white border-opacity-20
              whitespace-nowrap min-w-[80px] sm:min-w-[100px]
              ${ambitionLevel === level
                ? 'underline underline-offset-4 bg-white bg-opacity-10'
                : 'hover:opacity-80'}
            `}
          >
            {level === 'low' ? 'less' : 'more'} Change
          </button>

        ))}
      </div>
    );
  };

  // Function to update sector impact based on scenario and variant
  const updateSectorImpactForScenario = (scenario, variant = null) => {
    // Use current ambition level if no variant specified
    const finalVariant = variant || ambitionLevel;

    // Get the specific configuration for the scenario and variant
    const scenarioConfig = getScenarioConfig(scenario, finalVariant);

    // Set the sector impact to the configuration
    setSectorImpact(scenarioConfig);

    // Log for debugging
  };

  // Scenario Card Component
  const ScenarioCard = ({ scenario, isActive }) => {
    // Use current ambition level instead of local state
    const scenarioVariant = ambitionLevel;

    // Provide a default description if scenario is not found
    const { description = "You have chosen the panel's preferred scenario, with more support measures, where change is faster and may go on for longer. You have assumed moderate levels of other action to reduce energy use alongside higher levels of nature-based solutions for CDR." } =
      SCENARIO_DESCRIPTIONS[scenario]?.[scenarioVariant] ||
      SCENARIO_DESCRIPTIONS[scenario]?.current ||
      { description: "You have chosen the panel's preferred scenario, with more support measures, where change is faster and may go on for longer. You have assumed moderate levels of other action to reduce energy use alongside higher levels of nature-based solutions for CDR." };

    // Check if the current scenario is selected
    const isCurrentScenario = scenario &&
      Object.entries(sectorImpact).every(
        ([key, value]) => value === SCENARIO_CONFIGS[scenario]?.[scenarioVariant]?.[key]
      );

    // Predefined color classes for scenarios
    const colorClasses = {
      current: {
        bg: '',
        text: 'text-white',
        ring: ''
      },
      steer: {
        bg: '',
        text: 'text-white',
        ring: ''
      },
      shift: {
        bg: '',
        text: 'text-white',
        ring: ''
      },
      transform: {
        bg: '',
        text: 'text-white',
        ring: ''
      }
    }[scenario] || {
      bg: 'bg-white bg-opacity-10',
      text: 'text-white',
      ring: 'ring-white'
    };

    // If no scenario is provided, return null
    if (!scenario) return null;

    return (
      <div
        className={`flex flex-col p-6 rounded-lg transition-all duration-300 w-full min-h-32
          cursor-pointer w-full
          ${isActive ? 'scale-105' : ''}
          ${isCurrentScenario
            ? `${colorClasses.bg}`
            : `${colorClasses.bg} `}
          ${colorClasses.text}`}
        onClick={() => navigateToScenario(scenario, ambitionLevel)}
      >
        <div className="w-full justify-between items-start sm:items-center mb-0">

           {/* Show current ambition level */}

          <span className="flex-row text-center text-[1em] lg:text-[2em] md:text-[2.5em] sm:leading-[1em] md:leading-[1em] lg:leading-[1em] font-bold mb-0 text-white rounded clear-both">
              {description}
          </span>
        </div>
      </div>
    );
  };

  // Function to check if a scenario is fully selected
  const isScenarioFullySelected = () => {
    const currentScenario = scenarioKeys[activeScenarioIndex];

    // Check if the current sector impact matches any valid scenario configuration
    for (const [scenarioName, scenarioVariants] of Object.entries(SCENARIO_CONFIGS)) {
      for (const [variant, config] of Object.entries(scenarioVariants)) {
        const isMatch = Object.entries(config).every(([sector, impact]) =>
          sectorImpact[sector] === impact
        );
        if (isMatch) {
        //   console.log(`Scenario fully selected: ${scenarioName} with ${variant} ambition`);
          return true;
        }
      }
    }

    // console.log('No complete scenario match found');
    return false;
  };

  // Function to get total impact summary
  const getTotalImpactSummary = () => {
    const totalImpact = calculateTotalImpact(sectorImpact);
    return totalImpact.toFixed(1);
  };

  // Render View Impact Results Button
  const renderViewImpactResultsButton = () => {
    const isFullySelected = isScenarioFullySelected();

    // Log details for debugging
    // console.log('View Impact Results Button:');
    // console.log('Active Scenario Index:', activeScenarioIndex);
    // console.log('Current Scenario:', scenarioKeys[activeScenarioIndex]);
    // console.log('Sector Impact:', sectorImpact);
    // console.log('Is Fully Selected:', isFullySelected);

    return (
      <div className="relative w-full pb-12">
        <div className="align-center mt-2">
          <button
            onClick={() => {
            //   console.log('View Impact Results clicked');
            //   console.log('Is Fully Selected:', isFullySelected);
              isFullySelected && scrollToSection('results');
            }}
            disabled={!isFullySelected}
            className={`px-6 py-3 rounded-lg
              transition-colors duration-300
              flex items-center justify-center mx-auto space-x-2
              shadow-md
              ${isFullySelected
                ? 'bg-blue-600 text-white hover:bg-blue-700 cursor-pointer'
                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              }`}
          >
            <span>View Impact Results</span>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fillRule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-11a1 1 0 10-2 0v3.586L7.707 9.293a1 1 0 00-1.414 1.414l3 3a1 1 0 001.414 0l3-3a1 1 0 00-1.414-1.414L11 10.586V7z"
                clipRule="evenodd"
              />
            </svg>
          </button>

          {!isFullySelected && (
            <p className="text-xs text-gray-600 mt-2 text-center">
              Select a scenario to view impact results
            </p>
          )}
        </div>
      </div>
    );
  };

  // Render scenario carousel
  const renderScenarioCarousel = () => {
    const scenarioBackground = getCurrentScenarioBackground();

    return (
      <div className="relative w-full max-w-full mx-auto py-8">
        {/* Carousel Container */}
        <div className="relative w-full flex justify-center items-center">
          {/* Scenario Cards Container */}
          <div className="flex justify-center items-center w-full">
            {/* Focused Scenario (center, full size) */}
            <div
              className={`transform scale-100 z-20 rounded-lg transition-all duration-300 w-full max-w-[920px]`}
              onClick={handleScenarioSelect}
            >
              <ScenarioCard
                scenario={scenarioKeys[activeScenarioIndex]}
                isActive={true}
              />
            </div>
          </div>
        </div>
      </div>
    );
  };
             console.log(SCENARIO_LOOKUP);
  return (
    <div className="flex flex-col bg-gray-50">
      {/* Header */}
      <DashboardHeader />

      {/* Main Content */}
      <div className="flex flex-col flex-grow w-full">
        {/* Scenario Controls */}
        <div
          ref={choicesRef}
          id="your-choices"
          className="w-full mb-0 transition-all duration-500 ease-in-out"
          style={{
            backgroundColor: getCurrentScenarioBackground().backgroundColor,
            backgroundImage: getCurrentScenarioBackground().image
              ? `linear-gradient(to bottom, ${getCurrentScenarioBackground().overlay} 0%, ${getCurrentScenarioBackground().overlay} 100%), url(${getCurrentScenarioBackground().image})`
              : 'none',
            backgroundSize: 'contain',
            backgroundPosition: 'center top',
            backgroundRepeat: 'no-repeat'
          }}
        >
          <div className="container mx-auto px-2 sm:px-4 lg:px-6">
            <div className="flex flex-col items-center justify-center mt-0 mb-2 sm:mb-4 border-b border-white border-opacity-20 pb-0 pt-4 sm:pt-6 lg:pt-8 text-center bg-black bg-opacity-20 backdrop-blur-lg border border-white border-opacity-20">
              <h2 className="text-xl sm:text-2xl lg:text-3xl text-white font-semibold mb-2">Select Your Path to Net Zero</h2>
              <p className="text-white text-xs sm:text-sm lg:text-base max-w-sm sm:max-w-xl opacity-80 mb-4 sm:mb-6 px-2">
               Choose your panel-led scenario and your background assumptions to see the impact across the whole system.
              </p>

              {/* Side by side selection */}
              <div className="flex flex-col sm:flex-col lg:flex-row gap-4 sm:gap-6 lg:gap-8 items-center justify-center w-full max-w-6xl">
                {/* Step 1: Ambition Level Selection */}
                <div className="flex flex-col items-center w-full lg:w-auto">
                  <h3 className="text-sm sm:text-base lg:text-lg text-white font-medium mb-2 sm:mb-3 text-left">Step 1: Choose your panel-led scenario</h3>
                  {renderAmbitionLevelSelection()}
                </div>

                {/* Step 2: Scenario Selection */}
                <div className="flex flex-col  w-full lg:w-auto">
                  <h3 className="text-sm sm:text-base lg:text-lg text-white font-medium mb-2 sm:mb-3 text-left">Step 2: Choose your background assumptions</h3>
                  {renderScenarioSelectionButtons()}
                </div>
              </div>
            </div>

            {/* Scenario Carousel */}
            {renderScenarioCarousel()}

            {/* Comparison Section */}
            {renderComparisonSection()}





            {/* View Impact Results Button */}
            {renderViewImpactResultsButton()}

             {/* Results Section - Show for selected scenarios */}

        {isScenarioFullySelected() && (
          <>
            <div ref={resultsRef} id="results" className="container mx-auto p-6 mb-0">
              <h2 className="text-2xl text-white font-semibold mb-3">
               Scenario Results: {scenarioCopy}
              </h2>
            </div>

            {/* Results Tabs */}
            <ResultsTabs
              ref={impactsRef}
              activeTab={activeTab}
              setActiveTab={setActiveTab}
              sectorData={sectorData}
              sectorImpact={sectorImpact}
              ambitionLevel={ambitionLevel}
            />
          </>
        )}
          </div>
        </div>


      </div>

      {/* Quick Choices Panel */}
      {isQuickChoicesOpen && (
        <QuickChoicesPanel
          sectorData={sectorData}
          sectorImpact={sectorImpact}
          updateSectorImpact={updateSectorImpact}
          setIsQuickChoicesOpen={setIsQuickChoicesOpen}
          scrollToSection={scrollToSection}
          ambitionLevel={ambitionLevel}
          setAmbitionLevel={setAmbitionLevel}
        />
      )}

      {/* Floating Quick Choices Button */}
      {isQuickChoicesButtonVisible && (
        <QuickChoicesButton
          isQuickChoicesOpen={isQuickChoicesOpen}
          setIsQuickChoicesOpen={setIsQuickChoicesOpen}
          sectorImpact={sectorImpact}
        />
      )}
    </div>
  );
};

export default DashboardWireframe;
